import { PriceItem } from '@/features/common/components/Overview/types'
import { useOrderDraftPricesCache } from '@/features/pos/components/OrderFinalization/hooks/useOrderDraftPricesCache'
import { formatPrice } from '@/utils/priceHelper'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Prices {
    items: PriceItem[]
    total: string
}

export const usePrices = (loading: boolean): Prices | undefined => {
    const { t } = useTranslation()

    const orderDraftPrices = useOrderDraftPricesCache()

    return useMemo(() => {
        if (!orderDraftPrices) return undefined

        const items: PriceItem[] = []

        const loadingPricePlaceholder = loading ? '...' : undefined

        if (orderDraftPrices.productsPrice) {
            items.push({
                label: t('common.overview.prices.items'),
                price:
                    loadingPricePlaceholder ??
                    formatPrice(
                        orderDraftPrices.productsPrice.amount,
                        orderDraftPrices.productsPrice.currency
                    ),
            })
        }

        if (orderDraftPrices.smallOrderSurcharge) {
            items.push({
                label: t('common.overview.prices.smallOrderSurcharge'),
                price:
                    loadingPricePlaceholder ??
                    formatPrice(
                        orderDraftPrices.smallOrderSurcharge.amount,
                        orderDraftPrices.smallOrderSurcharge.currency
                    ),
            })
        }

        if (orderDraftPrices.deliveryPrice) {
            items.push({
                label: t('common.overview.prices.delivery'),
                price:
                    loadingPricePlaceholder ??
                    formatPrice(
                        orderDraftPrices.deliveryPrice.amount,
                        orderDraftPrices.deliveryPrice.currency
                    ),
            })
        }

        if (orderDraftPrices.containerFee) {
            items.push({
                label: t('common.overview.prices.container'),
                price:
                    loadingPricePlaceholder ??
                    formatPrice(
                        orderDraftPrices.containerFee.amount,
                        orderDraftPrices.containerFee.currency
                    ),
            })
        }

        const total =
            loadingPricePlaceholder ??
            (orderDraftPrices.totalPrice
                ? formatPrice(
                      orderDraftPrices.totalPrice.amount,
                      orderDraftPrices.totalPrice.currency
                  )
                : '...')

        return { items, total }
    }, [orderDraftPrices, loading, t])
}
